// Journal "Export .html" (Jade, 2026-10-28). It used to save the note body's
// HTML in an empty page: no background, no title, no stickers or text boxes,
// and the app's own styles (checklists, quotes, fonts) left behind. Now it is
// built like Save as Image: from a copy of the note as it's drawn on screen
// (JournalTab's makeNoteCopy), so the page, the formatted text, the stickers
// and the text boxes keep their places. These helpers turn that copy into a
// standalone file:
// - collectMatchingCss: the app's style rules that apply inside the copy,
//   with @media rules decided as they are on this device right now;
// - cssVarNames: the colour variables (var(--c-text) & co.) the copy uses, so
//   their current values can be written into the file;
// - inlineLocalUrls: app files and not-yet-uploaded images (a path on this
//   device, or a blob:) become data URLs, so the file shows them anywhere;
// - fontLinks: Google Fonts links for the note's fonts (the app bundles them,
//   the file loads them; offline, the browser's own font stands in);
// - buildNoteHtmlDocument: the page itself.

import { FONTS } from "../constants/fonts";

// Pseudo-elements and states can't be tested with querySelector; a rule with
// one is kept when the element part of it matches.
const PSEUDO_ELEMENT = /::?(before|after|placeholder|selection|marker|first-line|first-letter|backdrop|-webkit-[\w-]+|-moz-[\w-]+)/g;
const STATE = /:(hover|focus-visible|focus-within|focus|active|visited|link|target)\b/g;

export function testableSelector(selector) {
  return selector.split(",").map(s => s.replace(PSEUDO_ELEMENT, "").replace(STATE, "").trim() || "*").join(",");
}

function applies(selector, root) {
  try {
    const t = testableSelector(selector);
    return root.matches(t) || !!root.querySelector(t);
  } catch {
    return false;
  }
}

// Style rules that apply to `root` or anything inside it. @media / @supports
// blocks are kept only if they hold now, and written without the wrapper, so
// the file looks as the note does on this device (not as a wide desktop
// window would re-style it). Sheets from another site can't be read and are
// skipped (the fonts come from fontLinks instead).
export function collectMatchingCss(root, sheets = document.styleSheets) {
  const out = [];
  const visit = (rules) => {
    Array.from(rules || []).forEach(rule => {
      if (rule.selectorText != null) {
        if (applies(rule.selectorText, root)) out.push(rule.cssText);
      } else if (rule.media && rule.cssRules) {
        try { if (window.matchMedia(rule.media.mediaText).matches) visit(rule.cssRules); } catch { /* bad query */ }
      } else if (rule.conditionText != null && rule.cssRules) {
        try { if (window.CSS && CSS.supports(rule.conditionText)) visit(rule.cssRules); } catch { /* bad condition */ }
      }
    });
  };
  Array.from(sheets || []).forEach(sheet => {
    let rules = null;
    try { rules = sheet.cssRules; } catch { rules = null; }
    visit(rules);
  });
  return out;
}

export function cssVarNames(text) {
  const names = new Set();
  const re = /var\(\s*(--[\w-]+)/g;
  let m;
  while ((m = re.exec(text))) names.add(m[1]);
  return [...names];
}

// Weights each family really has on Google Fonts (asking for one it lacks
// makes Google refuse the whole request). Pacifico has a single weight.
const FONT_WEIGHTS = {
  Quicksand: "400;500;600;700", Nunito: "400;500;600;700;800", Poppins: "400;500;600;700;800",
  Lato: "400;700;900", Raleway: "400;500;600;700;800", Pacifico: "", Comfortaa: "400;500;600;700",
  Caveat: "400;500;600;700",
};

// The note's fonts: any of the app's fonts named in the copy or its styles.
export function usedFonts(text) {
  return FONTS.filter(f => new RegExp(`\\b${f}\\b`).test(text));
}

export function fontLinks(families) {
  return families.map(f => {
    const w = FONT_WEIGHTS[f];
    const fam = encodeURIComponent(f).replace(/%20/g, "+") + (w ? `:wght@${w}` : "");
    return `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=${fam}&display=swap">`;
  }).join("\n");
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// Whether a URL only works inside the app (its own files on this device, or a
// picture still being uploaded). Those are copied into the file.
export function isLocalUrl(url, base = window.location.href) {
  try {
    const u = new URL(url, base);
    if (u.protocol === "data:") return false;
    return u.protocol === "blob:" || u.origin === new URL(base).origin;
  } catch {
    return false;
  }
}

// Rewrites every src="..." and url(...) inside `root` that only works in the
// app into a data URL. Any other address is made absolute. A file that can't
// be read keeps its address.
export async function inlineLocalUrls(root, fetchImpl = (u) => fetch(u)) {
  const cache = new Map();
  const toData = (url) => {
    const abs = new URL(url, window.location.href).href;
    if (!isLocalUrl(abs)) return Promise.resolve(abs);
    if (!cache.has(abs)) {
      cache.set(abs, fetchImpl(abs)
        .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
        .then(blobToDataUrl)
        .catch(() => abs));
    }
    return cache.get(abs);
  };
  const els = [root, ...root.querySelectorAll("*")];
  const jobs = [];
  els.forEach(el => {
    ["src", "poster"].forEach(attr => {
      const v = el.getAttribute(attr);
      if (v && !v.startsWith("data:")) jobs.push(toData(v).then(nv => el.setAttribute(attr, nv)));
    });
    const style = el.getAttribute("style");
    if (style && style.includes("url(")) {
      const urls = [];
      style.replace(/url\(\s*(['"]?)(.*?)\1\s*\)/g, (m, q, u) => { if (u && !u.startsWith("data:") && !u.startsWith("#")) urls.push(u); return m; });
      if (urls.length) jobs.push(Promise.all(urls.map(toData)).then(vals => {
        let next = el.getAttribute("style");
        urls.forEach((u, i) => { next = next.split(u).join(vals[i]); });
        el.setAttribute("style", next);
      }));
    }
  });
  await Promise.all(jobs);
}

const escapeHtml = (s) => String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// The page. `body` is the copied note (already holding its own background,
// colours and fonts); `width` is the note's width on screen, which the
// stickers' places depend on, so a phone opening the file zooms to it.
export function buildNoteHtmlDocument({ title, body, css = [], fonts = [], width, dark = false, wrapperClass = "", deskBg = "#ececec", extra = "" }) {
  return `<!DOCTYPE html>
<html lang="en"${dark ? " data-dark" : ""}>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=${Math.round(width)}">
<title>${escapeHtml(title)}</title>
${fontLinks(fonts)}
<style>
${css.join("\n")}
html, body { margin: 0; padding: 0; }
body { background: ${deskBg}; }
*, *::before, *::after { animation: none !important; transition: none !important; }
.cj-export-page { width: ${Math.round(width)}px; margin: 0 auto; overflow: hidden; }
@media (max-width: ${Math.round(width) + 47}px) {
  .cj-export-page { min-height: 100vh; }
}
@media (min-width: ${Math.round(width) + 48}px) {
  body { padding: 24px 0; }
  .cj-export-page { border-radius: 14px; box-shadow: 0 6px 30px rgba(0,0,0,0.18); }
}
</style>
</head>
<body>
<div class="${escapeHtml(wrapperClass)}">${body}</div>
${extra}
</body>
</html>
`;
}
